/* F-05k: smart header. States: static (in the page, below the film), hidden and
   shown (pinned to the top). Elements marked data-es-smart-header-avoid, such as
   a pinned scroll-story stage, are never covered. */
(() => {
 if (customElements.get('elmsett-smart-header')) return;
 const AVOID = '[data-es-smart-header-avoid],elmsett-scroll-story[data-enhanced]';
 const INTENT = 10;
 // The pair sits 14px down and is 44px tall, so this is the screen row it occupies.
 const PAIR_BOTTOM = 58;
 // The film carries its own play/pause button on the same edge as the bag, so it is
 // retired while it is still this far clear of the pair's row rather than passing behind it.
 const PAIR_CLEAR = 60;
 class ElmsettSmartHeader extends HTMLElement {
  connectedCallback() {
   if (this.abort) return;
   this.abort = new AbortController();
   const signal = this.abort.signal, header = this.querySelector('.es-header');
   this.bar = this.querySelector('.es-smart-header__bar') || this;
   this.menu = this.querySelector('.es-mobile-menu');
   this.brand = this.querySelector('.es-brand-link');
   this.state = 'static'; this.dataset.state = 'static';
   // F-33: the opening film is a phone-sized problem; from 750px nothing changes.
   this.film = document.querySelector('.es-film--immersive');
   this.phone = matchMedia('(max-width: 749px)');
   document.documentElement.dataset.esSmartHeader = 'static';
   this.lastY = this.scrollTop(); this.travel = 0; this.frame = 0; this.keyboard = false; this.quietUntil = 0;
   const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
   addEventListener('scroll', schedule, { passive: true, signal });
   addEventListener('resize', schedule, { passive: true, signal });
   this.phone.addEventListener('change', schedule, { signal });
   addEventListener('pageshow', schedule, { signal });
   addEventListener('hashchange', () => this.quiet(), { signal });
   document.addEventListener('click', event => {
    const link = event.target instanceof Element && event.target.closest('a[href*="#"]');
    if (link && link.hash && link.origin === location.origin && link.pathname === location.pathname) this.quiet();
   }, { capture: true, signal });
   document.addEventListener('keydown', event => { if (event.key === 'Tab') this.keyboard = true; }, { capture: true, signal });
   document.addEventListener('pointerdown', () => { this.keyboard = false; }, { capture: true, signal });
   // F-33: over an avoid zone the returning header is the compact pair, so focus
   // brings that back rather than a solid bar across the stage it must not cover.
   this.addEventListener('focusin', () => { if (this.state === 'hidden') this.set(this.phone?.matches && this.covered() ? 'over' : 'shown'); }, { signal });
   this.menu?.addEventListener('toggle', schedule, { signal });
   // The shared menu script scrolls the header into view on open; a pinned header already is.
   if (header) {
    header.scrollIntoView = (...args) => { if (this.state === 'static' && !this.hasAttribute('data-es-pair')) HTMLElement.prototype.scrollIntoView.apply(header, args); };
    this.release = () => { delete header.scrollIntoView; };
   }
   this.update();
  }
  disconnectedCallback() {
   this.abort?.abort(); this.abort = null;
   cancelAnimationFrame(this.frame); this.frame = 0;
   this.release?.();
   this.removeAttribute('data-es-pair');
   delete document.documentElement.dataset.esFilmExit;
   this.filmCue?.removeAttribute('tabindex');
   this.handedOn = null;
   if (this.brand) this.brand.tabIndex = 0;
   delete document.documentElement.dataset.esSmartHeader;
  }
  scrollTop() {
   const max = Math.max(0, document.documentElement.scrollHeight - innerHeight);
   return Math.min(Math.max(scrollY, 0), max);
  }
  quiet() {
   // Same-page anchor jumps land with their target clear of the header.
   this.quietUntil = performance.now() + 800; this.travel = 0;
   if (this.state === 'shown') this.set('hidden');
  }
  locked() {
   return Boolean(this.menu?.open) || Boolean(document.querySelector('cart-drawer.active')) || document.body.classList.contains('overflow-hidden');
  }
  covered() {
   const limit = this.bar.offsetHeight;
   for (const element of document.querySelectorAll(AVOID)) {
    const rect = element.getBoundingClientRect();
    if (rect.top < limit && rect.bottom > 0) return true;
   }
   return false;
  }
  update() {
   const y = this.scrollTop(), delta = y - this.lastY;
   this.lastY = y;
   // F-33: while the opening film still covers the strip the pair sits in, the menu
   // and the bag ride above it. The header keeps its own place in the page, so the
   // page does not move when the film scrolls away. Once the film's bottom rises
   // past the pair, the header itself is on screen and the pair retires, which also
   // keeps it from wearing its on-photograph look over the page below.
   const onFilm = Boolean(this.phone?.matches && this.film && this.film.getBoundingClientRect().bottom > PAIR_BOTTOM);
   if (onFilm !== this.hasAttribute('data-es-pair')) {
    this.toggleAttribute('data-es-pair', onFilm);
    // The header's own brand link is below the film while the pair is out, so leaving
    // it in the tab order would drag the page down between the menu and the bag.
    if (this.brand) this.brand.tabIndex = onFilm ? -1 : 0;
   }
   // F-33: the opening film's own play/pause button (F-44) sits at the film's bottom
   // right, on the bag's edge, and rides up with the film. Left alone it would pass
   // behind the pair, where a tap meant for it opens the bag. It leaves while it is
   // still clear of them, and comes back the same way when the film is scrolled down.
   if (!this.filmCue?.isConnected) this.filmCue = this.film?.querySelector('.es-film-toggle') ?? null;
   const exit = Boolean(this.phone?.matches && this.filmCue && this.filmCue.getBoundingClientRect().top < PAIR_BOTTOM + PAIR_CLEAR);
   let handover = null;
   if (exit !== ('esFilmExit' in document.documentElement.dataset)) {
    if (exit) {
     // F-33a: a retiring button is not a tab stop, and when it is the one holding
     // keyboard focus the pair's menu takes it: the next control in the same order,
     // drawn where the button was heading. Left alone the focus falls to the body
     // and the ring goes out until the visitor presses Tab again.
     // Who holds the focus is read before the attribute is written: with reduce
     // motion the fade is dropped, so visibility:hidden lands in the same frame and
     // the browser blurs the button before the next read of activeElement.
     const held = this.filmCue.contains(document.activeElement);
     document.documentElement.dataset.esFilmExit = '';
     this.filmCue.tabIndex = -1;
     if (held) handover = this.menu?.querySelector('summary') ?? this.film?.querySelector('.es-film-scroll');
     handover?.focus({preventScroll: true});
    } else {
     delete document.documentElement.dataset.esFilmExit;
     this.filmCue?.removeAttribute('tabindex');
    }
   }
   this.settle(delta);
   // F-62, F-65: once the retiring button has handed a keyboard visitor's focus to the
   // menu, the header stays with that focus for as long as it rests there. When the
   // header's own place leaves the top of the screen — a jump, End, or an ordinary
   // scroll of several frames — it comes down pinned, as the pair over a pinned stage,
   // so the ring never goes out above the screen and the next Tab does not land on a
   // wordmark the pair is about to hide. Pointer and touch visitors are not held.
   if (handover && this.keyboard && this.contains(handover)) this.handedOn = handover;
   if (this.handedOn && (!this.keyboard || !exit || document.activeElement !== this.handedOn)) this.handedOn = null;
   if (this.handedOn && this.getBoundingClientRect().top < 0) this.set(this.covered() ? 'over' : 'shown');
  }
  settle(delta) {
   const slot = this.getBoundingClientRect();
   if (slot.top >= 0) { this.travel = 0; this.set('static'); return; }
   if (this.locked()) { this.travel = 0; return; }
   if (this.state === 'static') { if (slot.bottom <= 0) { this.travel = 0; this.set('hidden'); } return; }
   // A jump of more than a screen is navigation (anchor, restore), not a swipe.
   // F-33 changes phones only, so from 750px an avoid zone still silences the bar.
   const compact = Boolean(this.phone?.matches);
   if (performance.now() < this.quietUntil || Math.abs(delta) > innerHeight || (!compact && this.covered())) { this.travel = 0; this.set('hidden'); return; }
   // F-33: on a phone an avoid zone no longer silences the bar; over one the bar
   // returns as the compact pair, which sits clear of a pinned stage's heading.
   if (compact && this.state === 'shown' && this.covered()) this.set('over');
   else if (this.state === 'over' && (!compact || !this.covered())) this.set('shown');
   if (!delta) return;
   this.travel = Math.sign(delta) === Math.sign(this.travel) ? this.travel + delta : delta;
   if (this.travel <= -INTENT) this.set(compact && this.covered() ? 'over' : 'shown');
   else if (this.travel >= INTENT && slot.bottom <= 0 && !(this.keyboard && this.contains(document.activeElement))) this.set('hidden');
  }
  set(state) {
   // F-65: the pair hides the wordmark, so while it holds a keyboard visitor's focus
   // the full bar stays; turning into the pair would drop that focus to the page.
   if (state === 'over' && this.keyboard && this.brand?.contains(document.activeElement)) state = 'shown';
   if (state === this.state) return;
   const pinned = state !== 'static', wasPinned = this.state !== 'static';
   if (pinned !== wasPinned) this.removeAttribute('data-animate');
   this.state = state; this.dataset.state = state;
   document.documentElement.dataset.esSmartHeader = state;
   if (pinned && !wasPinned) {
    this.bar.getBoundingClientRect();
    requestAnimationFrame(() => { if (this.state !== 'static') this.setAttribute('data-animate', ''); });
   }
  }
 }
 customElements.define('elmsett-smart-header', ElmsettSmartHeader);
})();
